
import React, { useEffect, useRef, useState } from "react";
import "./FunctionsSettings.scss";
import { projectFirestore, timestamp } from "../../firebase-config/config";

/* Small UI Modal */
function UiModal({ open, title, message, onClose }) {
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(onClose, 2000);
    return () => clearTimeout(timer);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="ui-modal-overlay">
      <div className="ui-modal">
        {title && <div className="ui-modal-title">{title}</div>}
        <div className="ui-modal-body">{message}</div>
      </div>
    </div>
  );
}

/* Chip Component */
function Chip({ value, idx, onUpdate, onRemove, readOnly, actionsDisabled }) {
  const [editing, setEditing] = useState(value === "" && !readOnly);
  const [val, setVal] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const finish = () => {
    const next = val.trim();
    setEditing(false);
    onUpdate(idx, next);
  };

  return (
    <div className="email-chip">
      {editing ? (
        <input
          ref={inputRef}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") finish();
            if (e.key === "Escape") {
              setVal(value || "");
              setEditing(false);
            }
          }}
          onBlur={finish}
          className="chip-input"
          placeholder="Enter email"
          type="email"
        />
      ) : (
        <span className="chip-label" title={val}>
          {val}
        </span>
      )}

      {!readOnly && !actionsDisabled && (
        <div className="chip-actions">
          <button className="icon-btn icon-remove" onClick={() => onRemove(idx)}>
            ✕
          </button>
          <button className="icon-btn icon-edit" onClick={() => setEditing(true)}>
            ✎
          </button>
        </div>
      )}
    </div>
  );
}

/* Helper Label */
const prettifyLabel = (docId) =>
  docId
    .replace(/_temp$/, "")
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const emptyTemplateData = {
  ccList: [],
  bccList: [],
  toList: [],
  subject: "",
  body: "",
  status: false,
  updatedAt: null,
  btnName: "",
};

/* MAIN COMPONENT */
export default function FunctionsSettings() {
  const [templatesMeta, setTemplatesMeta] = useState([]);
  const [templatesData, setTemplatesData] = useState({});
  const [activeTemplate, setActiveTemplate] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editingSubject, setEditingSubject] = useState(false);
  const [editingBody, setEditingBody] = useState(false);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimerRef = useRef(null);

  const openModal = (t, m) => {
    setModalTitle(t);
    setModalMessage(m);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const showToast = (msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(msg);
    setToastOpen(true);
    toastTimerRef.current = setTimeout(() => setToastOpen(false), 4000);
  };

  const getField = (field) =>
    activeTemplate ? templatesData[activeTemplate]?.[field] : undefined;

  const setField = (field, value) =>
    setTemplatesData((prev) => ({
      ...prev,
      [activeTemplate]: {
        ...(prev[activeTemplate] || emptyTemplateData),
        [field]: value,
      },
    }));

  /* Fetch All Templates */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const col = projectFirestore.collection("email_templates");
        const snap = await col.get();

        const meta = [];
        const map = {};

        snap.forEach((doc) => {
          const d = doc.data();

          meta.push({
            id: doc.id,
            label: prettifyLabel(doc.id),
          });

          map[doc.id] = {
            ccList: d.ccList || [],
            bccList: d.bccList || [],
            toList: d.toList || [],
            subject: d.subject || "",
            body: d.body || "",
            status: !!d.status,
            updatedAt: d.updatedAt || null,
            btnName: d.btnName || prettifyLabel(doc.id),
          };
        });

        setTemplatesMeta(meta);
        setTemplatesData(map);

        if (!activeTemplate && meta.length > 0) setActiveTemplate(meta[0].id);
      } catch {
        setError("Failed to load templates");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const editingAllowed = () =>
    activeTemplate && templatesData[activeTemplate]?.status;

  const updateAt = (field, idx, val) => {
    const list = [...getField(field)];
    if (val.trim() === "") list.splice(idx, 1);
    else list[idx] = val;
    setField(field, list);
  };

  const removeAt = (field, idx) => {
    const list = [...getField(field)];
    list.splice(idx, 1);
    setField(field, list);
  };

  const addAtEnd = (field) => {
    const list = [...getField(field)];
    list.push("");
    setField(field, list);
  };

  const updateTemplateStatus = async (tplId, status) => {
    setTemplatesData((prev) => ({
      ...prev,
      [tplId]: { ...(prev[tplId] || {}), status },
    }));

    try {
      await projectFirestore.collection("email_templates").doc(tplId).set(
        {
          status,
          updatedAt: timestamp.fromDate(new Date()),
        },
        { merge: true }
      );
      showToast(status ? "Edit Mode ON" : "Edit Mode OFF");
    } catch {
      openModal("Error", "Failed to update status");
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await projectFirestore
        .collection("email_templates")
        .doc(activeTemplate)
        .set(
          {
            ccList: getField("ccList"),
            bccList: getField("bccList"),
            toList: getField("toList"),
            subject: getField("subject"),
            body: getField("body"),
            updatedAt: timestamp.fromDate(new Date()),
            btnName: templatesData[activeTemplate]?.btnName,
          },
          { merge: true }
        );

      showToast("Updated successfully");
    } catch {
      openModal("Error", "Failed to save template");
    } finally {
      setLoading(false);
      setEditingSubject(false);
      setEditingBody(false);
    }
  };

  return (
    <div className="fs-page-container">
      <div className="fs-layout">
        <div className="fs-wrapper-parent">
          <div className="section-title">
            <p> Function Setting</p>
            <h2> Customize Email Template</h2>
          </div>

          {/* CARD 1 – TABS */}
          <div className="fs-card fs-card-tabs">
            <div className="fs-tabs">
              {templatesMeta.map((tpl) => {
                const isActive = activeTemplate === tpl.id;
                const tplStatus = templatesData[tpl.id]?.status;

                const btnName =
                  templatesData[tpl.id]?.btnName || prettifyLabel(tpl.id);

                return (
                  <button
                    key={tpl.id}
                    className={`fs-tab ${isActive ? "active" : ""}`}
                    onClick={() => setActiveTemplate(tpl.id)}
                    type="button"
                  >
                    <span className="tab-label">{btnName}</span>

                    <label
                      className={`tpl-toggle ${tplStatus ? "on" : "off"}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={!!tplStatus}
                        onChange={(e) =>
                          updateTemplateStatus(tpl.id, e.target.checked)
                        }
                      />
                      <span className="toggle-track">
                        <span className="toggle-thumb" />
                      </span>
                    </label>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CARD 2 – BODY */}
          <div className="fs-card fs-card-body">
            <div className="fs-content">
              {error && <div className="error">{error}</div>}

              {/* CC */}
              <div className="fs-field">
                <span className="fs-field-label">Cc:</span>

                <div className="list-grid">
                  {(getField("ccList") || []).map((c, i) => (
                    <Chip
                      key={`cc-${i}-${c}-${Date.now()}`}
                      value={c}
                      idx={i}
                      onUpdate={(idx, val) => updateAt("ccList", idx, val)}
                      onRemove={(index) => removeAt("ccList", index)}
                      readOnly={!editingAllowed()}
                    />
                  ))}
                </div>

                {editingAllowed() && (
                  <button className="add-link" onClick={() => addAtEnd("ccList")}>
                    + Add Email
                  </button>
                )}
              </div>

              {/* BCC */}
              <div className="fs-field">
                <span className="fs-field-label">Bcc:</span>

                <div className="list-grid">
                  {(getField("bccList") || []).map((c, i) => (
                    <Chip
                      key={`bcc-${i}-${c}-${Date.now()}`}
                      value={c}
                      idx={i}
                      onUpdate={(idx, val) => updateAt("bccList", idx, val)}
                      onRemove={(index) => removeAt("bccList", index)}
                      readOnly={!editingAllowed()}
                    />
                  ))}
                </div>

                {editingAllowed() && (
                  <button className="add-link" onClick={() => addAtEnd("bccList")}>
                    + Add Email
                  </button>
                )}
              </div>

              {/* SUBJECT */}
              <div className="fs-field">
                <span className="fs-field-label">Subject:</span>

                <div className="input-card">
                  <input
                    ref={subjectRef}
                    className="input-full"
                    value={getField("subject") || ""}
                    readOnly={!editingSubject || !editingAllowed()}
                    onChange={(e) => setField("subject", e.target.value)}
                  />

                  {editingAllowed() && (
                    <button
                      className="icon-btn edit-field"
                      onClick={() => {
                        setEditingSubject(true);
                        setTimeout(() => subjectRef.current?.focus(), 0);
                      }}
                      type="button"
                    >
                      ✎
                    </button>
                  )}
                </div>
              </div>

              {/* BODY */}
              <div className="fs-field">
                <span className="fs-field-label">Body:</span>

                <div className="input-card">
                  <textarea
                    ref={bodyRef}
                    className="input-full"
                    rows="6"
                    value={getField("body") || ""}
                    readOnly={!editingBody || !editingAllowed()}
                    onChange={(e) => setField("body", e.target.value)}
                  />

                  {editingAllowed() && (
                    <button
                      className="icon-btn edit-field"
                      onClick={() => {
                        setEditingBody(true);
                        setTimeout(() => bodyRef.current?.focus(), 0);
                      }}
                      type="button"
                    >
                      ✎
                    </button>
                  )}
                </div>
              </div>

              {/* SAVE BUTTON */}
              <div className="fs-footer">
                <button
                  className="btn-save"
                  disabled={!editingAllowed()}
                  onClick={handleSave}
                  type="button"
                >
                  {loading ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>

          {/* MODAL */}
          <UiModal open={modalOpen} title={modalTitle} message={modalMessage} onClose={closeModal} />

          {/* TOAST */}
          {toastOpen && (
            <div className="ui-toast">
              <svg width="18" height="18" fill="white" viewBox="0 0 24 24">
                <path d="M20.285 6.708l-11.084 11.084-5.486-5.486 1.414-1.414 4.072 4.071 9.67-9.67z" />
              </svg>
              <span>{toastMessage}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}